/**
 * TODAS SEARCH PANEL
 * Inline sökpanel under headern (ersätter Horizons sökmodal när
 * temainställningen todas_search_layout = panel).
 *
 * Egen liten motor — inget beroende på Horizons predictive-search.js:
 *  - öppnas av Horizons sökknapp (<search-button> button) via egen click-lyssnare,
 *    ⌘K / Ctrl+K, eller [data-todas-search-open]
 *  - hämtar sections/todas-predictive-search.liquid via Shopifys
 *    predictive search-endpoint + section rendering
 *  - tangentbord: pil upp/ner, Enter, Esc
 *  - tomt läge (populära sökningar, kategorier, produkter) renderas server-side
 *    i snippets/todas-search-panel.liquid
 */
class TodasSearchPanel extends HTMLElement {
  #abort = null;
  #timer = null;
  #cache = new Map();
  #trigger = null;
  #open = false;
  #controller = new AbortController();

  connectedCallback() {
    const $ = (s) => this.querySelector(s);
    this.refs = {
      panel: $('.todas-search__panel'),
      form: $('.todas-search__form'),
      input: $('.todas-search__input'),
      clear: $('.todas-search__clear'),
      results: $('.todas-search__results'),
      empty: $('.todas-search__empty'),
      status: $('.todas-search__status'),
    };
    const { signal } = this.#controller;
    const { input, clear, form } = this.refs;

    // Horizons sökknappar (header + mobil-drawer). Horizons egen dispatcher
    // letar efter #search-modal som inte finns i panel-läget → tyst no-op.
    document.addEventListener(
      'click',
      (e) => {
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        const btn = t.closest('search-button button, [data-todas-search-open]');
        if (btn) {
          e.preventDefault();
          e.stopPropagation();
          this.toggle(btn);
          return;
        }
        if (this.#open && t.closest('[data-todas-search-close]')) {
          e.preventDefault();
          this.close();
        }
      },
      { signal, capture: true }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
          e.preventDefault();
          this.toggle();
        } else if (e.key === 'Escape' && this.#open) {
          e.preventDefault();
          this.close();
        }
      },
      { signal }
    );

    input.addEventListener('input', () => this.#onInput(), { signal });
    input.addEventListener('keydown', (e) => this.#onKeyDown(e), { signal });
    clear.addEventListener(
      'click',
      () => {
        input.value = '';
        this.#showEmpty();
        input.focus();
      },
      { signal }
    );
    form.addEventListener(
      'submit',
      (e) => {
        if (!input.value.trim()) e.preventDefault();
      },
      { signal }
    );
    window.addEventListener('resize', () => this.#open && this.#position(), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  toggle(trigger) {
    this.#open ? this.close() : this.open(trigger);
  }

  open(trigger) {
    if (this.#open) return;
    this.#open = true;
    this.#trigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.#position();
    this.hidden = false;
    document.documentElement.classList.add('todas-search-open');
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.refs.input.focus({ preventScroll: true });
      this.refs.input.select();
    });
    this.#trigger?.setAttribute?.('aria-expanded', 'true');
  }

  close() {
    if (!this.#open) return;
    this.#open = false;
    this.classList.remove('is-open');
    document.documentElement.classList.remove('todas-search-open');
    this.#trigger?.setAttribute?.('aria-expanded', 'false');
    const done = () => {
      if (!this.#open) this.hidden = true;
    };
    this.refs.panel.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 260);
    if (this.#trigger instanceof HTMLElement) this.#trigger.focus({ preventScroll: true });
  }

  /** Lägg panelen direkt under headern (eller i toppen om headern scrollat bort). */
  #position() {
    const header = document.querySelector('header-component') || document.querySelector('#header-component, .header');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--todas-search-top', `${bottom}px`);
  }

  #onInput() {
    const term = this.refs.input.value.trim();
    this.refs.clear.hidden = term.length === 0;
    clearTimeout(this.#timer);
    if (!term) {
      this.#showEmpty();
      return;
    }
    this.#timer = setTimeout(() => this.#search(term), 220);
  }

  #showEmpty() {
    this.#abort?.abort();
    this.refs.results.hidden = true;
    this.refs.results.innerHTML = '';
    this.refs.empty.hidden = false;
    this.refs.clear.hidden = true;
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.status.textContent = '';
  }

  async #search(term) {
    const { results, empty, input, status } = this.refs;
    this.#abort?.abort();
    const ac = (this.#abort = new AbortController());

    let html = this.#cache.get(term);
    if (!html) {
      const url = new URL(this.dataset.suggestUrl || '/search/suggest', location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,collection,article,page,query');
      url.searchParams.set('resources[limit]', this.dataset.limit || '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', this.dataset.sectionId || 'todas-predictive-search');
      status.textContent = 'Söker …';
      try {
        const res = await fetch(url, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        html = (doc.querySelector('.shopify-section') || doc.body).innerHTML;
        this.#cache.set(term, html);
      } catch (err) {
        if (ac.signal.aborted) return;
        status.textContent = 'Sökningen misslyckades – försök igen.';
        return;
      }
    }
    if (ac.signal.aborted) return;

    results.innerHTML = html;
    results.hidden = false;
    empty.hidden = true;
    input.setAttribute('aria-expanded', 'true');
    results.scrollTop = 0;
    const live = results.querySelector('[data-todas-search-status]');
    status.textContent = live ? live.textContent.trim() : '';
  }

  get #items() {
    return Array.from(this.refs.results.querySelectorAll('a[data-result]'));
  }

  #onKeyDown(e) {
    const items = this.#items;
    if (!items.length || this.refs.results.hidden) return;
    const current = items.findIndex((el) => el.getAttribute('aria-selected') === 'true');
    const select = (i) => {
      items.forEach((el, idx) => {
        if (idx === i) {
          el.setAttribute('aria-selected', 'true');
          el.classList.add('is-selected');
          el.scrollIntoView({ block: 'nearest' });
        } else {
          el.removeAttribute('aria-selected');
          el.classList.remove('is-selected');
        }
      });
    };
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      select(current < items.length - 1 ? current + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      select(current > 0 ? current - 1 : items.length - 1);
    } else if (e.key === 'Enter' && current >= 0) {
      e.preventDefault();
      items[current].click();
    }
  }
}

if (!customElements.get('todas-search-panel')) {
  customElements.define('todas-search-panel', TodasSearchPanel);
}
